import { useForm } from 'react-hook-form';
import { useModalStore, useWithdrawOperationData } from '@/shared/lib';
import {
  createWithdrawSbpFormSchema,
  type WithdrawAmountValues,
  type WithdrawSbpFormValues,
} from './';
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import {
  type FiatWithdrawAssetOption,
  getFiatWithdrawAssetOptions,
  useWithdrawTotalAmount,
} from '@/entities/operation';
import { useUserProfileInfo } from '@/entities/user';
import { useSbpPaymentType } from '@/shared/api';
import { useWithdrawCommission } from '../lib';
import { useBankOptions } from '@/features/withdraw-sbp/lib/useBankOptions.ts';

export function useWithdrawSbpForm() {
  const { t } = useTranslation();
  const { getState } = useModalStore();
  const [selectedCurrency, setSelectedCurrency] = useState<
    FiatWithdrawAssetOption | undefined
  >();
  const [totalAmount, setTotalAmount] = useState(0);
  const [phoneError, setPhoneError] = useState<string>('');

  const { userProfileInfoMap, isUserProfileInfoLoading } = useUserProfileInfo();
  const userPhone = userProfileInfoMap.phone || '';
  const initFormData = getState('withdrawSbpFormData') as
    | WithdrawSbpFormValues
    | undefined;

  const {
    ewallet,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
    depositWithdrawAssetsIsError,
    depositWithdrawAssetsData,
    assetsIconsData,
    assetsData,
    mainAccount,
  } = useWithdrawOperationData();

  const assetOptions = useMemo<FiatWithdrawAssetOption[]>(() => {
    return getFiatWithdrawAssetOptions({
      key: 'sbp',
      assetsData,
      assetsIconsData,
      depositWithdrawAssetsData,
      mainAccount,
      ewallet,
    });
  }, [
    assetsData,
    assetsIconsData,
    ewallet,
    depositWithdrawAssetsData,
    mainAccount,
  ]);

  const validationSchema = useMemo(() => {
    const phoneErrorMessage = t('withdrawMethods.sbp.phoneError');
    return createWithdrawSbpFormSchema(
      selectedCurrency?.min,
      selectedCurrency?.available,
      totalAmount,
      phoneError || phoneErrorMessage,
      selectedCurrency?.max,
      t('common.maxAmount', {
        amount: selectedCurrency?.max,
        currency: selectedCurrency?.code ?? '',
      })
    );
  }, [
    selectedCurrency?.max,
    selectedCurrency?.available,
    selectedCurrency?.code,
    selectedCurrency?.min,
    totalAmount,
    phoneError,
    t,
  ]);

  const form = useForm<WithdrawSbpFormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      currencyCode: initFormData?.currencyCode || '',
      phoneNumber: initFormData?.phoneNumber || userPhone,
      bankId: initFormData?.bankId || '',
      amount: initFormData?.amount || '',
      includeFee: initFormData?.includeFee || false,
    },
  });

  const watchedValues = form.watch();

  const {
    paymentTypeCode,
    isPaymentTypeLoading,
    isPaymentTypeError,
    paymentTypeError,
  } = useSbpPaymentType({
    direction: 'withdraw',
    currencyCode: watchedValues.currencyCode,
    enabled: !!watchedValues.currencyCode,
  });

  const { bankOptions, isBanksLoading, isBanksError, banksError } =
    useBankOptions(paymentTypeCode);

  useEffect(() => {
    if (userPhone && !initFormData?.phoneNumber) {
      form.setValue('phoneNumber', userPhone);
      form.trigger('phoneNumber');
    }
  }, [userPhone, initFormData, form]);

  useEffect(() => {
    if (userPhone) {
      const phoneStartsWithPlus7 = userPhone.startsWith('+7');
      if (!phoneStartsWithPlus7) {
        setPhoneError(t('withdrawMethods.sbp.phoneError'));
      } else {
        setPhoneError('');
      }
    }
  }, [userPhone, t]);

  useEffect(() => {
    if (assetOptions.length === 1 && !initFormData?.currencyCode) {
      form.setValue(
        'currencyCode',
        assetOptions[0].code || watchedValues.currencyCode
      );
    } else if (initFormData?.currencyCode) {
      form.setValue('currencyCode', initFormData.currencyCode);
    }
  }, [
    assetOptions,
    watchedValues.currencyCode,
    form,
    initFormData?.currencyCode,
  ]);

  useEffect(() => {
    if (bankOptions.length === 1 && !initFormData?.bankId) {
      form.setValue('bankId', bankOptions[0].id || watchedValues.bankId);
    } else if (initFormData?.bankId) {
      form.setValue('bankId', initFormData.bankId);
    }
  }, [bankOptions, watchedValues.bankId, form, initFormData?.bankId]);

  useEffect(() => {
    const currencyItem = assetOptions.find(
      a => a.code === watchedValues.currencyCode
    );

    if (currencyItem) {
      setSelectedCurrency(currencyItem);
    }
  }, [watchedValues.currencyCode, assetOptions]);

  const amountError = form.formState.errors.amount;
  const hasAmountError = !!amountError;

  const { commissionData, commissionError, isGettingCommission } =
    useWithdrawCommission({
      amount: Number(watchedValues.amount),
      selectedCurrency,
      hasAmountError,
      onTop: watchedValues.includeFee,
      paymentTypeCode,
    });

  const [amount, includeFee] = form.watch(['amount', 'includeFee']);

  const amountValues: WithdrawAmountValues = {
    amount: String(amount),
    includeFee: includeFee ?? false,
  };
  const { totalAmount: calculatedTotalAmount } = useWithdrawTotalAmount<
    WithdrawSbpFormValues,
    WithdrawAmountValues
  >({
    form,
    watchedValues: amountValues,
    commissionData,
    assetCode: selectedCurrency?.code,
    isCommissionLoading: isGettingCommission,
    precision: NUMERIC_CONFIG.precision.default,
    additionalDependencies: [selectedCurrency?.code],
    validationDependencies: [
      amountValues.amount,
      selectedCurrency?.code,
      totalAmount,
    ],
  });

  useEffect(() => {
    setTotalAmount(calculatedTotalAmount);
  }, [calculatedTotalAmount]);

  const handleMaxClick = () => {
    if (selectedCurrency) {
      form.setValue('amount', String(selectedCurrency.available));
    }
  };

  return {
    form,
    watchedValues,
    assetOptions,
    selectedCurrency,
    bankOptions,
    totalAmount,
    selectedBank: bankOptions.find(b => b.id === watchedValues.bankId),
    hasWithdrawFee: isGettingCommission
      ? false
      : commissionData
        ? commissionData.feeAmount > 0
        : false,
    paymentTypeCode,
    isFromDataLoading:
      depositWithdrawAssetsLoading ||
      isPaymentTypeLoading ||
      isBanksLoading ||
      isUserProfileInfoLoading,
    isFormDataError:
      depositWithdrawAssetsIsError || isPaymentTypeError || isBanksError,
    formDataError: depositWithdrawAssetsError || paymentTypeError || banksError,
    feeAmount: commissionData?.feeAmount || 0,
    error: commissionError,
    handleMaxClick,
    isLoading: isGettingCommission,
    userPhone,
  };
}
